import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Users, Plus, Trash2, Eye } from 'lucide-react';

const STAGE_OPTIONS = ['Prospect', 'Aware', 'Engaged', 'Qualified', 'Won', 'Lost'];

function CreateSegmentDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [stages, setStages] = useState([]);
  const [tagsText, setTagsText] = useState('');
  const [withinDays, setWithinDays] = useState('');
  const [phonesText, setPhonesText] = useState('');
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  const buildFilters = () => ({
    lead_stages: stages.length ? stages : undefined,
    tags: tagsText.split(',').map((t) => t.trim()).filter(Boolean) || undefined,
    last_message_within_days: withinDays ? parseInt(withinDays, 10) : undefined,
    custom_phones: phonesText.split('\n').map((p) => p.trim()).filter(Boolean) || undefined,
  });

  const previewSegment = async () => {
    setLoading(true);
    try {
      const res = await api.post('/api/segments/preview', { filters: buildFilters() });
      setPreview(res);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  const submit = async () => {
    setLoading(true);
    try {
      await api.post('/api/segments', {
        name, description, filters: buildFilters(),
      });
      toast.success('Segment created');
      onCreated?.();
      setOpen(false);
      setName('');
      setDescription('');
      setStages([]);
      setTagsText('');
      setWithinDays('');
      setPhonesText('');
      setPreview(null);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleStage = (s) =>
    setStages((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-[#00D1B2] hover:bg-[#00B89C] text-white gap-2" data-testid="create-segment-btn">
          <Plus className="w-4 h-4" /> New Segment
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Create audience segment</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Name</Label>
            <Input data-testid="seg-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="High-intent festival shoppers" />
          </div>
          <div>
            <Label>Description (optional)</Label>
            <Input value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wide text-slate-400">Lead stages</Label>
            <div className="flex flex-wrap gap-1 mt-1">
              {STAGE_OPTIONS.map((s) => (
                <button
                  key={s}
                  type="button"
                  data-testid={`seg-stage-${s}`}
                  onClick={() => toggleStage(s)}
                  className={`text-[11px] px-2 py-1 rounded-md border ${stages.includes(s) ? 'bg-[#00D1B2] text-white border-[#00D1B2]' : 'bg-white text-slate-600 border-gray-200'}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px]">Tags (CSV)</Label>
              <Input value={tagsText} onChange={(e) => setTagsText(e.target.value)} placeholder="vip, diwali" />
            </div>
            <div>
              <Label className="text-[11px]">Active within (days)</Label>
              <Input type="number" value={withinDays} onChange={(e) => setWithinDays(e.target.value)} placeholder="30" />
            </div>
          </div>
          <div>
            <Label className="text-[11px]">Custom phones (one per line)</Label>
            <Textarea rows={3} value={phonesText} onChange={(e) => setPhonesText(e.target.value)} placeholder="+91…" />
          </div>
          {preview && (
            <div className="bg-slate-50 rounded-md p-3 text-xs" data-testid="seg-preview-result">
              <p className="font-semibold text-slate-700">Preview: {preview.size} contacts</p>
              {preview.sample?.length > 0 && (
                <p className="mt-1 text-slate-500 font-mono">{preview.sample.join(', ')}</p>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={previewSegment} disabled={loading} data-testid="seg-preview-btn" className="gap-2">
            <Eye className="w-4 h-4" /> Preview
          </Button>
          <Button onClick={submit} disabled={loading || !name} data-testid="seg-submit-btn" className="bg-[#00D1B2] hover:bg-[#00B89C] text-white">
            {loading ? 'Saving…' : 'Save segment'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function SegmentsPage() {
  const [segments, setSegments] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/api/segments');
      setSegments(r.segments || []);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete segment '${s.name}'?`)) return;
    try {
      await api.del(`/api/segments/${s.id}`);
      toast.success('Segment deleted');
      await load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-8" data-testid="segments-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Audience Segments</h2>
          <p className="text-sm text-slate-500 mt-1">
            Saved filters (by lead stage, tags, recency) you can reuse across Campaigns and Sequences.
          </p>
        </div>
        <CreateSegmentDialog onCreated={load} />
      </div>

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden" data-testid="segments-list">
        {loading ? (
          <div className="p-12 text-center text-sm text-slate-400">Loading…</div>
        ) : segments.length === 0 ? (
          <div className="p-16 text-center">
            <Users className="w-10 h-10 mx-auto text-slate-300 mb-3" />
            <p className="text-sm font-medium text-slate-700">No segments yet</p>
            <p className="text-xs text-slate-400 mt-1">Group contacts by lead stage, tags, or recency for re-use.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {segments.map((s) => (
              <div key={s.id} className="flex items-start justify-between gap-4 p-5 hover:bg-slate-50/50" data-testid={`seg-row-${s.id}`}>
                <div className="flex-1">
                  <p className="font-semibold text-slate-900 text-sm">{s.name}</p>
                  {s.description && <p className="text-xs text-slate-500 mt-0.5">{s.description}</p>}
                  <p className="text-[10px] text-slate-400 font-mono mt-1">
                    {Object.entries(s.filters)
                      .filter(([, v]) => v !== null && v !== undefined && (Array.isArray(v) ? v.length : true))
                      .map(([k, v]) => `${k}=${Array.isArray(v) ? v.join('|') : v}`)
                      .join('  ·  ')}
                  </p>
                </div>
                <Button variant="ghost" size="icon" onClick={() => remove(s)} className="text-slate-400 hover:text-rose-600">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
